/**
 * Supported UI languages — mirrors api-server/src/types/localization.ts
 * (intentional duplication, like the event types).
 */
export const SUPPORTED_LANGUAGES = ['en', 'en-GB', 'es-419', 'pt-BR', 'tr'] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** Regional variants collapse to a canonical supported code. */
const LANGUAGE_ALIASES: Record<string, LanguageCode> = {
  en: 'en',
  'en-US': 'en',
  'en-GB': 'en-GB',
  'en-UK': 'en-GB',
  es: 'es-419',
  'es-419': 'es-419',
  'es-MX': 'es-419',
  'es-AR': 'es-419',
  'es-PE': 'es-419',
  'es-CO': 'es-419',
  pt: 'pt-BR',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-BR',
  tr: 'tr',
  'tr-TR': 'tr',
  'tr-tr': 'tr',
};

export type LanguageFallbackReason = 'missing' | 'unsupported';

/**
 * Resolve an arbitrary value to a supported LanguageCode, canonicalizing
 * aliases (e.g. `es-MX` → `es-419`). Falls back to English instead of
 * throwing; `onFallback` lets the caller log with its own logger so this
 * module stays free of side effects (core is framework- and logger-agnostic).
 */
export function resolveLanguage(
  value: unknown,
  onFallback?: (reason: LanguageFallbackReason, value: unknown) => void
): LanguageCode {
  if (typeof value !== 'string' || value.trim() === '') {
    onFallback?.('missing', value);
    return DEFAULT_LANGUAGE;
  }

  const match = LANGUAGE_ALIASES[value.trim()];
  if (match) {
    return match;
  }

  onFallback?.('unsupported', value);
  return DEFAULT_LANGUAGE;
}

/**
 * Diagnose a `languageId` that arrived from a host and resolve it to a
 * supported code.
 *
 * The published lib forwards `languageId` untouched (ADR 0061), so the iframe —
 * served at runtime and therefore always current — is the only place that knows
 * the list, and the only place that can honestly say a code is unknown. Called
 * once at iframe init with the raw value the host actually sent.
 */
export function resolveLanguageWithWarning(
  value: unknown,
  warn: (message: string, context: { languageId: unknown }) => void
): LanguageCode {
  return resolveLanguage(value, (reason, raw) => {
    warn(
      reason === 'missing'
        ? 'languageId missing, defaulting to en'
        : 'Unsupported languageId, falling back to en',
      { languageId: raw }
    );
  });
}
